export const WORKDAY_ENTRY_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_acceptance', label: 'Pending Acceptance' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'schedule_declined', label: 'Withdrawn' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Awaiting Review' },
  { value: 'accepted', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'logged', label: 'Hours Logged' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

export const WORKDAY_ENTRY_STATUSES = WORKDAY_ENTRY_STATUS_OPTIONS.map(
  (option) => option.value,
)

export type WorkDayEntryStatus = (typeof WORKDAY_ENTRY_STATUS_OPTIONS)[number]['value']

export const WORKDAY_ENTRY_STATUS_LABELS: Record<WorkDayEntryStatus, string> =
  Object.fromEntries(
    WORKDAY_ENTRY_STATUS_OPTIONS.map((option) => [option.value, option.label]),
  ) as Record<WorkDayEntryStatus, string>

export const WORKDAY_ENTRY_STATUS_HINTS: Record<WorkDayEntryStatus, string> = {
  draft: 'Not scheduled yet.',
  pending_acceptance: 'Waiting for the worker to accept this schedule.',
  scheduled: 'Worker accepted the schedule. Work has not started.',
  schedule_declined: 'Withdrawn. This shift is no longer active.',
  in_progress: 'Worker has checked in.',
  completed: 'Worker submitted completion. Waiting for supervisor review.',
  accepted: 'Supervisor approved. This is the final status.',
  rejected: 'Supervisor rejected. This is the final status.',
  logged: 'Hours recorded without check-in or check-out.',
  cancelled: 'Cancelled because the assignment was deleted.',
}

const FINAL_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>(['accepted', 'rejected', 'cancelled'])
export const JOB_CLOSE_BLOCKING_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'in_progress',
  'completed',
])
export const JOB_COMPLETION_ALLOWED_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>(
  WORKDAY_ENTRY_STATUSES.filter((status) => !JOB_CLOSE_BLOCKING_WORKDAY_ENTRY_STATUSES.has(status)),
)
export const ASSIGNMENT_COMPLETION_ALLOWED_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'accepted',
  'rejected',
  'schedule_declined',
  'cancelled',
])
const EDITABLE_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'pending_acceptance',
  'scheduled',
  'schedule_declined',
  'rejected',
])
const PORTAL_SCHEDULE_EDITABLE_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'draft',
  'pending_acceptance',
  'scheduled',
  'schedule_declined',
  'rejected',
  'in_progress',
  'completed',
  'accepted',
  'logged',
])
const PAY_ADJUSTABLE_WORKDAY_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'pending_acceptance',
  'scheduled',
  'schedule_declined',
])

const MANUAL_STATUS_CHANGE_HIDDEN_TARGETS = new Set<WorkDayEntryStatus>([
  'draft',
  'logged',
  'pending_acceptance',
  'schedule_declined',
])

const WORKDAY_ENTRY_STATUS_TRANSITIONS: Record<WorkDayEntryStatus, readonly WorkDayEntryStatus[]> = {
  draft: ['scheduled', 'logged'],
  pending_acceptance: ['scheduled', 'schedule_declined', 'in_progress', 'rejected', 'draft'],
  schedule_declined: ['pending_acceptance', 'scheduled', 'in_progress', 'completed', 'rejected', 'draft'],
  scheduled: ['in_progress', 'completed', 'rejected', 'draft'],
  in_progress: ['completed', 'scheduled'],
  completed: ['accepted', 'rejected', 'in_progress'],
  accepted: ['completed', 'rejected'],
  rejected: ['completed', 'scheduled', 'pending_acceptance'],
  logged: ['scheduled', 'completed', 'accepted', 'rejected'],
  // Cancelled is a terminal state reached only when the assignment is deleted;
  // it has no outgoing transitions through the ordinary workflow.
  cancelled: [],
}

export function isWorkDayEntryAllowedForJobCompletion(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? JOB_COMPLETION_ALLOWED_WORKDAY_ENTRY_STATUSES.has(resolved) : true
}

export function isWorkDayEntryAllowedForAssignmentCompletion(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? ASSIGNMENT_COMPLETION_ALLOWED_WORKDAY_ENTRY_STATUSES.has(resolved) : true
}

export function getJobCompletionBlockedByWorkDayEntriesMessage(entries: Array<{ id?: string | number; status?: string | null; status_label?: string | null }>): string | null {
  const blocking = entries.filter((entry) => !isWorkDayEntryAllowedForJobCompletion(entry.status))
  if (blocking.length === 0) {
    return null
  }

  const details = blocking
    .slice(0, 3)
    .map((entry) => `#${entry.id ?? '?'} (${entry.status_label || getWorkDayEntryStatusLabel(entry.status)})`)
    .join(', ')
  const suffix = blocking.length > 3 ? `, and ${blocking.length - 3} more` : ''
  return `Complete or resolve work day entries before finishing the job. Pending: ${details}${suffix}. Only Approved, Rejected, or Draft entries are allowed.`
}

export function getAssignmentCompletionBlockedByWorkDayEntriesMessage(entries: Array<{ id?: string | number; status?: string | null; status_label?: string | null }>): string | null {
  if (entries.length === 0) {
    return 'Job assignments cannot be completed until the assigned worker has at least one work day entry.'
  }

  const blocking = entries.filter((entry) => !isWorkDayEntryAllowedForAssignmentCompletion(entry.status))
  if (blocking.length === 0) {
    return null
  }

  const details = blocking
    .slice(0, 3)
    .map((entry) => `#${entry.id ?? '?'} (${entry.status_label || getWorkDayEntryStatusLabel(entry.status)})`)
    .join(', ')
  const suffix = blocking.length > 3 ? `, and ${blocking.length - 3} more` : ''
  return `Job assignments cannot be completed if work day entries are not in Accepted, Rejected, Withdrawn. Pending: ${details}${suffix}.`
}

export function isFinalWorkDayEntryStatus(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? FINAL_WORKDAY_ENTRY_STATUSES.has(resolved) : false
}

export function getWorkDayEntryStatusHint(status?: string | null): string | null {
  const resolved = resolveWorkDayEntryStatus(status)
  if (!resolved) {
    return null
  }

  return WORKDAY_ENTRY_STATUS_HINTS[resolved]
}

export function getWorkDayEntryStatusChangeLabel(status: WorkDayEntryStatus): string {
  const label = WORKDAY_ENTRY_STATUS_LABELS[status]
  return isFinalWorkDayEntryStatus(status) ? `${label} (final)` : label
}

export function getAllowedWorkDayEntryStatusTransitions(
  status?: string | null,
): WorkDayEntryStatus[] {
  const resolved = resolveWorkDayEntryStatus(status)
  if (!resolved) {
    return []
  }

  return WORKDAY_ENTRY_STATUS_TRANSITIONS[resolved].filter(
    (value) => !MANUAL_STATUS_CHANGE_HIDDEN_TARGETS.has(value),
  )
}

export function getAdminWorkDayEntryStatusTargets(
  status?: string | null,
): WorkDayEntryStatus[] {
  const resolved = resolveWorkDayEntryStatus(status)
  if (!resolved) {
    return [...WORKDAY_ENTRY_STATUSES]
  }

  return [...WORKDAY_ENTRY_STATUS_TRANSITIONS[resolved]]
}

export function resolveWorkDayEntryStatus(raw?: string | null): WorkDayEntryStatus | null {
  const value = String(raw ?? '').trim().toLowerCase()
  if (!value) {
    return null
  }

  return WORKDAY_ENTRY_STATUSES.includes(value as WorkDayEntryStatus)
    ? (value as WorkDayEntryStatus)
    : null
}

export function resolveWorkDayEntryStatusFromLabel(label?: string | null): WorkDayEntryStatus | null {
  const normalized = String(label ?? '').trim().toLowerCase()
  if (!normalized) {
    return null
  }

  const match = WORKDAY_ENTRY_STATUS_OPTIONS.find(
    (option) => option.label.trim().toLowerCase() === normalized,
  )
  return match?.value ?? null
}

export function resolveWorkDayEntryStatusFromEntry(entry?: {
  status?: string | null
  status_label?: string | null
} | null): WorkDayEntryStatus | null {
  if (!entry) {
    return null
  }

  return resolveWorkDayEntryStatus(entry.status) ?? resolveWorkDayEntryStatusFromLabel(entry.status_label)
}

export function getWorkDayEntryStatusLabel(status?: string | null): string {
  const resolved = resolveWorkDayEntryStatus(status)
  if (!resolved) {
    return 'Unknown'
  }

  return WORKDAY_ENTRY_STATUS_LABELS[resolved]
}

export function getWorkDayEntryStatusBadgeClassName(status?: string | null): string {
  switch (resolveWorkDayEntryStatus(status)) {
    case 'pending_acceptance':
      return 'bg-orange-50 text-orange-800 border-orange-200'
    case 'schedule_declined':
      return 'bg-rose-50 text-rose-700 border-rose-200'
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'accepted':
      return 'bg-teal-50 text-teal-800 border-teal-300'
    case 'rejected':
      return 'bg-rose-50 text-rose-700 border-rose-200'
    case 'in_progress':
      return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'scheduled':
      return 'bg-sky-50 text-sky-700 border-sky-200'
    case 'logged':
      return 'bg-violet-50 text-violet-700 border-violet-200'
    case 'cancelled':
      return 'bg-slate-100 text-slate-600 border-slate-300'
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200'
  }
}

export type WorkDayEntryTimelineColor = {
  badge: string
  accent: string
}

const WORKDAY_ENTRY_STATUS_ACCENT_GRADIENTS: Record<WorkDayEntryStatus, string> = {
  draft: 'from-slate-400 via-slate-300 to-slate-200',
  pending_acceptance: 'from-orange-500 via-amber-400 to-yellow-400',
  scheduled: 'from-sky-500 via-cyan-400 to-blue-400',
  schedule_declined: 'from-rose-500 via-red-400 to-pink-400',
  in_progress: 'from-amber-500 via-orange-400 to-yellow-400',
  completed: 'from-emerald-500 via-green-400 to-teal-400',
  accepted: 'from-teal-500 via-emerald-400 to-cyan-400',
  rejected: 'from-rose-500 via-red-400 to-pink-400',
  logged: 'from-violet-500 via-purple-400 to-fuchsia-400',
  cancelled: 'from-slate-400 via-slate-300 to-slate-200',
}

export function getWorkDayEntryStatusTimelineColor(entry?: {
  status?: string | null
  status_label?: string | null
} | null): WorkDayEntryTimelineColor {
  const resolved = resolveWorkDayEntryStatusFromEntry(entry)
  if (!resolved) {
    return {
      badge: 'border-slate-200 bg-slate-50 text-slate-700',
      accent: WORKDAY_ENTRY_STATUS_ACCENT_GRADIENTS.draft,
    }
  }

  return {
    badge: getWorkDayEntryStatusBadgeClassName(resolved),
    accent: WORKDAY_ENTRY_STATUS_ACCENT_GRADIENTS[resolved],
  }
}

export function canDeleteWorkDayEntry(status?: string | null): boolean {
  return resolveWorkDayEntryStatus(status) === 'draft'
}

export function canEditWorkDayEntry(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? EDITABLE_WORKDAY_ENTRY_STATUSES.has(resolved) : false
}

export function canEditWorkDayEntrySchedule(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? PORTAL_SCHEDULE_EDITABLE_WORKDAY_ENTRY_STATUSES.has(resolved) : true
}

export function getWorkDayEntryScheduleEditBlockedMessage(status?: string | null): string {
  const statusLabel = getWorkDayEntryStatusLabel(status)
  return `Planned and actual times cannot be edited while this entry is ${statusLabel}. Use Change status to move it back to Scheduled or In Progress first.`
}

export function canAdjustWorkDayEntryPay(status?: string | null): boolean {
  const resolved = resolveWorkDayEntryStatus(status)
  return resolved ? PAY_ADJUSTABLE_WORKDAY_ENTRY_STATUSES.has(resolved) : false
}

export function getWorkDayEntryEditBlockedMessage(status?: string | null): string {
  return `Work day entries with status ${getWorkDayEntryStatusLabel(status)} cannot be edited.`
}

export function getWorkDayEntryDeleteBlockedMessage(status?: string | null): string {
  const statusLabel = getWorkDayEntryStatusLabel(status)
  return `Only work day entries with status Draft can be deleted. This entry is ${statusLabel}.`
}

export function getWorkDayEntryPayAdjustBlockedMessage(status?: string | null): string {
  const statusLabel = getWorkDayEntryStatusLabel(status).toLowerCase()
  return `Pay can only be adjusted while the entry is scheduled. This entry is ${statusLabel}.`
}

export const WORKDAY_ENTRY_WORKFLOW_NOTE =
  'Status is derived from the entry: check-in → In Progress, check-out → Awaiting Review. Admins can override status from the actions menu.'
